/**
 * Content Compliance Settings tab (Org Settings page).
 *
 * Two visual sections:
 * 1. "Regulated Entity" toggle — flips org between standard and regulated mode.
 * 2. "Compliance Defaults" — only visible when regulated. Configures expiration
 *    defaults, approval requirements, send-time enforcement, and overrides.
 *
 * Used by: src/app/admin/organization-settings/page.tsx (case 'compliance').
 */

import { useEffect, useState } from 'react';
import { Shield, AlertTriangle, CheckCircle2, ChevronRight } from 'lucide-react';
import ApprovalsPage from '@/src/app/admin/approvals/page';
import AssetPoliciesPage from '@/src/app/admin/asset-policies/page';
import ComplianceAuditPage from '@/src/app/admin/compliance-audit/page';
import ApprovalWorkflowsPage from '@/src/app/admin/approval-workflows/page';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  contentComplianceAPI,
  ContentComplianceSettings as Settings,
} from '@/services/api/contentCompliance';
import { useOrganization } from '@/contexts/OrganizationContext';

interface Props {
  organizationId: number;
  organizationName: string;
}

const FIELD_LABELS: Record<keyof Settings, { label: string; help: string }> = {
  cbo_id: { label: '', help: '' },
  mode: { label: '', help: '' },
  default_expiration_required: {
    label: 'Require expiration date on new media assets',
    help: 'If on, uploaded assets must have an expiration date unless explicitly opted out.',
  },
  default_expiration_days: {
    label: 'Default expiration window (days)',
    help: 'Used when no asset-type policy overrides this.',
  },
  allow_expired_in_communications: {
    label: 'Allow expired assets in communications',
    help: 'When off, the send-time gate blocks campaigns that reference expired assets.',
  },
  notify_days_before: {
    label: 'Warn this many days before expiration',
    help: 'Drives the "Expiring soon" badge and notifications.',
  },
  default_template_approval_required: {
    label: 'Require approval before publishing communication templates',
    help: 'Templates move draft → in_review → approved → published.',
  },
  default_content_approval_required: {
    label: 'Require approval for new media assets',
    help: 'Assets sit in pending_approval until reviewed.',
  },
  default_workflow_approval_required: {
    label: 'Require approval for workflow templates',
    help: 'Workflow editors gain a review step before activation.',
  },
  approval_levels: {
    label: 'Approval levels',
    help: '1 = single reviewer. 2 = two-step review.',
  },
  enforce_consent_on_all_paths: {
    label: 'Enforce member consent on all outbound paths',
    help: 'Extends the existing per-recipient suppression check beyond bulk and staff sends.',
  },
  block_expired_in_communications: {
    label: 'Block sends that reference expired media',
    help: 'Hard block at the composer; admins with override permission may still proceed with reason.',
  },
  require_approved_template_for_send: {
    label: 'Require an approved template for every send',
    help: 'Sends fail if the chosen template is not in published state.',
  },
  allow_compliance_override: {
    label: 'Allow compliance overrides',
    help: 'When off, blocks are absolute — no override path.',
  },
  override_requires_reason: {
    label: 'Require a reason for every override',
    help: 'Reason is captured in the audit log.',
  },
};

type SubView = 'settings' | 'asset-policies' | 'approvals' | 'compliance-audit' | 'approval-workflows';

export function ContentComplianceSettings({ organizationId, organizationName }: Props) {
  const { refetch } = useOrganization();
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirmFlip, setConfirmFlip] = useState<'enable' | 'disable' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [subView, setSubView] = useState<SubView>('settings');

  const isRegulated = settings?.mode === 'regulated';

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    contentComplianceAPI
      .getSettings(organizationId)
      .then((s) => mounted && setSettings(s))
      .catch((e) => mounted && setError(e?.response?.data?.detail || e.message))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, [organizationId]);

  // Drill-down views render full-bleed inside the Compliance tab content area,
  // with a back link returning to the settings form. Placed AFTER all hooks
  // to avoid a conditional-hook count change on subView toggle.
  // We pass isRegulated explicitly because the embedded pages' useComplianceMode
  // hook reads from OrganizationContext, which can be stale relative to the
  // authoritative /api/compliance/settings response we already have here.
  if (subView === 'asset-policies') {
    return (
      <AssetPoliciesPage
        onBack={() => setSubView('settings')}
        isRegulatedOverride={isRegulated}
      />
    );
  }
  if (subView === 'approvals') {
    return (
      <ApprovalsPage
        onBack={() => setSubView('settings')}
        isRegulatedOverride={isRegulated}
      />
    );
  }
  if (subView === 'compliance-audit') {
    return (
      <ComplianceAuditPage
        onBack={() => setSubView('settings')}
        organizationId={organizationId}
      />
    );
  }
  if (subView === 'approval-workflows') {
    return (
      <ApprovalWorkflowsPage
        onBack={() => setSubView('settings')}
        isRegulatedOverride={isRegulated}
      />
    );
  }

  const flipMode = async (mode: 'standard' | 'regulated') => {
    setSaving(true);
    setError(null);
    try {
      const result = await contentComplianceAPI.setMode(organizationId, mode);
      setSettings(result.settings);
      await refetch();
    } catch (e: any) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
      setConfirmFlip(null);
    }
  };

  const updateField = async (key: keyof Settings, value: any) => {
    if (!settings) return;
    setSaving(true);
    setError(null);
    const next = { ...settings, [key]: value };
    setSettings(next);
    try {
      const result = await contentComplianceAPI.updateSettings(organizationId, { [key]: value });
      setSettings(result);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e.message);
      setSettings(settings); // revert
    } finally {
      setSaving(false);
    }
  };

  if (loading || !settings) {
    return <div className="p-6 text-sm text-gray-500">Loading compliance settings…</div>;
  }

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Section 1 — Regulated Entity toggle */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Regulated Entity
            {isRegulated ? (
              <Badge className="bg-amber-100 text-amber-900 border-amber-200">
                Regulated mode
              </Badge>
            ) : (
              <Badge variant="outline">Standard mode</Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Regulated mode enables expiration policies on media and documents and requires
            admin-approved templates before they can be sent. Use this for organizations operating
            under HIPAA, state Medicaid, or other regulated programs (e.g., Utah).
          </p>
          <div className="flex items-center justify-between rounded-md border p-4">
            <div>
              <Label className="text-sm font-medium">
                Treat <span className="font-semibold">{organizationName}</span> as a regulated entity
              </Label>
              <p className="text-xs text-gray-500 mt-1">
                Flipping this changes behavior across templates, media library, and send composer.
              </p>
            </div>
            <Switch
              checked={isRegulated}
              disabled={saving}
              onCheckedChange={(checked) => setConfirmFlip(checked ? 'enable' : 'disable')}
            />
          </div>
          {isRegulated && (
            <div className="text-xs text-gray-500 flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3 text-green-600" />
              All compliance defaults below are now enforced.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick links — only visible when regulated */}
      {isRegulated && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Configure compliance details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <button
              type="button"
              onClick={() => setSubView('approval-workflows')}
              className="w-full flex items-center justify-between rounded-md border p-3 hover:bg-gray-50 dark:hover:bg-gray-900 text-sm text-left"
            >
              <div>
                <div className="font-medium">Approval workflows</div>
                <div className="text-xs text-gray-500">
                  Define who reviews templates, media, and workflow templates before they go live.
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-gray-400" />
            </button>
            <button
              type="button"
              onClick={() => setSubView('asset-policies')}
              className="w-full flex items-center justify-between rounded-md border p-3 hover:bg-gray-50 dark:hover:bg-gray-900 text-sm text-left"
            >
              <div>
                <div className="font-medium">Asset policies</div>
                <div className="text-xs text-gray-500">
                  Per-asset-type expiration and approval rules.
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-gray-400" />
            </button>
            <button
              type="button"
              onClick={() => setSubView('approvals')}
              className="w-full flex items-center justify-between rounded-md border p-3 hover:bg-gray-50 dark:hover:bg-gray-900 text-sm text-left"
            >
              <div>
                <div className="font-medium">Approval queue</div>
                <div className="text-xs text-gray-500">
                  Pending submissions for templates, media, and workflows.
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-gray-400" />
            </button>
            <button
              type="button"
              onClick={() => setSubView('compliance-audit')}
              className="w-full flex items-center justify-between rounded-md border p-3 hover:bg-gray-50 dark:hover:bg-gray-900 text-sm text-left"
            >
              <div>
                <div className="font-medium">Compliance audit</div>
                <div className="text-xs text-gray-500">
                  Expiring artifacts, overrides, and enforcement events.
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-gray-400" />
            </button>
          </CardContent>
        </Card>
      )}

      {/* Section 2 — Compliance defaults (only when regulated) */}
      {isRegulated && (
        <Card>
          <CardHeader>
            <CardTitle>Compliance Defaults</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <SettingsBlock
              title="Content expiration"
              fields={[
                'default_expiration_required',
                'default_expiration_days',
                'allow_expired_in_communications',
                'notify_days_before',
              ]}
              settings={settings}
              onChange={updateField}
              saving={saving}
            />
            <SettingsBlock
              title="Approval requirements"
              fields={[
                'default_template_approval_required',
                'default_content_approval_required',
                'default_workflow_approval_required',
                'approval_levels',
              ]}
              settings={settings}
              onChange={updateField}
              saving={saving}
            />
            <SettingsBlock
              title="Send-time enforcement"
              fields={[
                'enforce_consent_on_all_paths',
                'block_expired_in_communications',
                'require_approved_template_for_send',
              ]}
              settings={settings}
              onChange={updateField}
              saving={saving}
            />
            <SettingsBlock
              title="Compliance overrides"
              fields={['allow_compliance_override', 'override_requires_reason']}
              settings={settings}
              onChange={updateField}
              saving={saving}
            />
          </CardContent>
        </Card>
      )}

      {/* Confirm modal */}
      <Dialog open={confirmFlip !== null} onOpenChange={(o) => !o && setConfirmFlip(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {confirmFlip === 'enable'
                ? 'Enable Regulated Entity mode?'
                : 'Disable Regulated Entity mode?'}
            </DialogTitle>
            <DialogDescription className="pt-2">
              {confirmFlip === 'enable' ? (
                <>
                  Enabling this turns on:
                  <ul className="list-disc ml-6 mt-2 space-y-1">
                    <li>Approval workflows on communication templates and media assets.</li>
                    <li>Send-time blocks for expired media and unapproved templates.</li>
                    <li>Hard consent enforcement on every outbound path.</li>
                  </ul>
                  <p className="mt-3">Users in this org will see the Compliance UI immediately.</p>
                </>
              ) : (
                <>
                  Disabling this:
                  <ul className="list-disc ml-6 mt-2 space-y-1">
                    <li>Pauses pending approval requests (kept for audit).</li>
                    <li>Removes send-time blocks; existing data is preserved.</li>
                    <li>Existing per-recipient consent checks remain in place.</li>
                  </ul>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmFlip(null)} disabled={saving}>
              Cancel
            </Button>
            <Button
              onClick={() => flipMode(confirmFlip === 'enable' ? 'regulated' : 'standard')}
              disabled={saving}
            >
              {saving ? 'Saving…' : confirmFlip === 'enable' ? 'Enable Regulated mode' : 'Disable'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SettingsBlock({
  title,
  fields,
  settings,
  onChange,
  saving,
}: {
  title: string;
  fields: (keyof Settings)[];
  settings: Settings;
  onChange: (key: keyof Settings, value: any) => void;
  saving: boolean;
}) {
  return (
    <div>
      <h4 className="text-sm font-semibold mb-3">{title}</h4>
      <div className="space-y-3">
        {fields.map((field) => {
          const meta = FIELD_LABELS[field];
          if (!meta || !meta.label) return null;
          const value = (settings as any)[field];
          const isNumber = field === 'default_expiration_days' ||
            field === 'notify_days_before' ||
            field === 'approval_levels';
          return (
            <div
              key={field as string}
              className="flex items-start justify-between gap-4 rounded-md border p-3"
            >
              <div className="flex-1">
                <Label className="text-sm font-medium">{meta.label}</Label>
                <p className="text-xs text-gray-500 mt-1">{meta.help}</p>
              </div>
              {isNumber ? (
                <Input
                  type="number"
                  value={value ?? ''}
                  className="w-24"
                  disabled={saving}
                  onBlur={(e) => onChange(field, e.target.value === '' ? null : Number(e.target.value))}
                  onChange={(e) => {
                    // optimistic local update only
                    (settings as any)[field] = e.target.value === '' ? null : Number(e.target.value);
                  }}
                />
              ) : (
                <Switch
                  checked={Boolean(value)}
                  disabled={saving}
                  onCheckedChange={(v) => onChange(field, v)}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default ContentComplianceSettings;
